import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10 } from "styled-components";
import React from "react";
import { gsap } from "gsap";
import { Slider, type Shape } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { Icon } from "~/features/ui/icon";
import CaretLeft from "~/public/icons/caret-left.svg";
import CaretRight from "~/public/icons/caret-right.svg";
import { SectionHeader } from "./header";
const EASE = "cubic-bezier(0.125, 0.425, 0.27, 1)";
type Props = {
  children: React.ReactNode[];
  eyebrow?: React.ReactNode;
  headline?: string;
  text?: string;
  link?: React.ReactNode;
  hasHeaderData: boolean;
  filterBar?: React.ReactNode;
  filterKey?: string;
  perPage?: number;
  hasShowcaseCards?: boolean;
  headlineStart?: boolean;
};
export function Default({
  children,
  hasHeaderData,
  eyebrow,
  headline,
  text,
  link,
  filterBar,
  filterKey,
  perPage = 3,
  hasShowcaseCards,
  headlineStart
}: Props) {
  const sliderRef = React.useRef<Shape>(null);
  const sliderContainerRef = React.useRef<HTMLDivElement>(null);
  const animationRef = React.useRef<gsap.core.Tween | null>(null);
  const isFirstRender = React.useRef(true);
  const [sliderIndex, setSliderIndex] = React.useState(0);
  const [lastSlideIndex, setLastSlideIndex] = React.useState(0);
  const items = children;
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    animationRef.current?.kill();
    const container = sliderContainerRef.current;
    if (!container) return;
    setSliderIndex(0);
    sliderRef.current?.go(0);
    requestAnimationFrame(() => {
      const slides = container.querySelectorAll(".splide__slide");
      if (slides.length === 0) return;
      animationRef.current = gsap.fromTo(slides, {
        y: 20,
        opacity: 0
      }, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: EASE,
        stagger: 0.08
      });
    });
  }, [filterKey]);
  React.useEffect(() => {
    return () => {
      animationRef.current?.kill();
    };
  }, []);
  return <_StyledDiv $_css={[{
    "position": "relative",
    "zIndex": "10",
    "display": "none",
    "@media (min-width: 1024px)": {
      "display": "block"
    }
  }]}>
      {(hasHeaderData || filterBar) && <_StyledDiv2 $_css2={[{
      "zIndex": "10",
      "display": "flex",
      "gap": "var(--spacing-lg)",
      "padding": "var(--spacing-3xl)"
    }]}>
          <SectionHeader alignment="horizontal" eyebrow={eyebrow} headline={headline} text={text} link={link} filterBar={filterBar} headlineStart={headlineStart} />
        </_StyledDiv2>}
      <_StyledDiv3 ref={sliderContainerRef} $_css3={[{
      "paddingLeft": "var(--spacing-xl)",
      "paddingRight": "var(--spacing-xl)",
      "paddingBottom": "var(--spacing-2xl)"
    }, hasShowcaseCards && {
      "paddingTop": "4rem"
    }]}>
        <Slider ref={sliderRef} onMounted={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} onMove={splide => {
        setSliderIndex(splide.Components.Controller.getIndex());
        setLastSlideIndex(splide.Components.Controller.getEnd());
      }} onResized={splide => setLastSlideIndex(splide.Components.Controller.getEnd())} options={{
        perPage,
        gap: 16
      }}>
          {items.map((child, idx) => <Slider.Slide key={idx}>{child}</Slider.Slide>)}
        </Slider>
      </_StyledDiv3>
      {lastSlideIndex > 0 && <_StyledDiv4 $_css4={[{
      "display": "flex",
      "alignItems": "center",
      "gap": "var(--spacing-lg)",
      "paddingLeft": "var(--spacing-xl)",
      "paddingRight": "var(--spacing-xl)",
      "paddingBottom": "var(--spacing-xl)"
    }]}>
          <_StyledDiv5 $_css5={[{
        "flex": "1 1 0%"
      }]}>
            <Progress activeSlideIndex={sliderIndex} totalSlides={lastSlideIndex + 1} />
          </_StyledDiv5>
          <_StyledDiv6 $_css6={[{
        "display": "flex"
      }]}>
            <_StyledButton type="button" onClick={() => sliderRef.current?.go("<")} disabled={sliderIndex === 0} $_css7={[{
          "display": "flex",
          "height": "3.2rem",
          "width": "3.2rem",
          "alignItems": "center",
          "justifyContent": "center",
          "borderRadius": "0",
          "borderBottomLeftRadius": "4px",
          "borderTopLeftRadius": "4px",
          "backgroundColor": "rgba(0, 7, 4, 0.2) !important",
          "color": "var(--text-primary)",
          "transitionProperty": "opacity"
        }]}>
              <_StyledIcon icon={CaretLeft} size="16" $_css8={[sliderIndex === 0 && {
            "opacity": "0.3"
          }]} />
            </_StyledButton>
            <_StyledButton2 type="button" onClick={() => sliderRef.current?.go(">")} disabled={sliderIndex >= lastSlideIndex} $_css9={[{
          "display": "flex",
          "height": "3.2rem",
          "width": "3.2rem",
          "alignItems": "center",
          "justifyContent": "center",
          "borderRadius": "0",
          "borderBottomRightRadius": "4px",
          "borderTopRightRadius": "4px",
          "backgroundColor": "rgba(0, 7, 4, 0.2) !important",
          "color": "var(--text-primary)",
          "transitionProperty": "opacity"
        }]}>
              <_StyledIcon2 icon={CaretRight} size="16" $_css10={[sliderIndex >= lastSlideIndex && {
            "opacity": "0.3"
          }]} />
            </_StyledButton2>
          </_StyledDiv6>
        </_StyledDiv4>}
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1678p1p-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1678p1p-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1678p1p-2"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-1678p1p-3"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-1678p1p-4"
})`${p => p.$_css5}`;
var _StyledDiv6 = _styled6("div").withConfig({
  componentId: "sc-sf-1678p1p-5"
})`${p => p.$_css6}`;
var _StyledButton = _styled7("button").withConfig({
  componentId: "sc-sf-1678p1p-6"
})`${p => p.$_css7}`;
var _StyledIcon = _styled8(Icon).withConfig({
  componentId: "sc-sf-1678p1p-7"
})`${p => p.$_css8}`;
var _StyledButton2 = _styled9("button").withConfig({
  componentId: "sc-sf-1678p1p-8"
})`${p => p.$_css9}`;
var _StyledIcon2 = /*#__PURE__*/_styled10(Icon).withConfig({
  displayName: "default___StyledIcon2",
  componentId: "sc-1mwdbhu-0"
})(["", ""], p => p.$_css10);